// @flow

import React, {useRef, useState} from 'react';
import CustomModal from '../../../../../../elements/CustomModal/CustomModal';
import {Divider, Grid, Typography, makeStyles} from '@material-ui/core';
import CustomButton from '../../../../../../elements/CustomButton/CustomButton';
import {colors} from '../../../../../../assets/styles/colors';

import squareLayout from '../../../../../../assets/img/product-sections/square-layout.png';
import landscapeLayout from '../../../../../../assets/img/product-sections/landscape-layout.png';
import portraitLayout from '../../../../../../assets/img/product-sections/portrait-layout.png';

import {CheckCircle, RadioButtonUnchecked} from '@material-ui/icons';
import CropModal from '../../../../../../elements/CropModal';
import type {ProductSectionImageLayout} from '../../../../../../entities';
import type {ImageToCrop} from '../../../../../../elements/CropModal';
type Props = {
  open: boolean,
  handleClose: () => void,
  onImageSave: (image: ImageToCrop, layout: ProductSectionImageLayout) => void,
};

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    width: 700,
    maxWidth: '100%',
    padding: theme.spacing(4, 1, 2, 1),
  },
  divider: {
    margin: theme.spacing(2, 0),
    backgroundColor: colors.grey300,
  },
  title: {
    textAlign: 'left',
    fontWeight: 600,
    fontSize: 16,
  },
  btn: {
    backgroundColor: '#F2F2F2',
    borderRadius: '10px',
    position: 'relative',
    padding: theme.spacing(5),
  },
  saveBtnWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: theme.spacing(2, 0),
  },
  saveBtn: {
    width: 'fit-content',
  },
  contentImg: {
    width: '100%',
    height: '100%',
    position: 'relative',
  },
  checkboxWrap: {
    position: 'absolute',
    left: theme.spacing(1.5),
    top: theme.spacing(1.5),
  },
}));

const SECTION_IMAGE_LAYOUTS = [
  {name: 'SQUARE', img: squareLayout},
  {name: 'LANDSCAPE', img: landscapeLayout},
  {name: 'PORTRAIT', img: portraitLayout},
];

const ASPECT_RATIOS = {
  SQUARE: 1,
  LANDSCAPE: 1.78,
  PORTRAIT: 0.5625,
};

export default function SelectImageLayoutModal(props: Props) {
  const {open, handleClose, onImageSave} = props;
  const classes = useStyles();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [file, setFile] = useState<File | null>(null);

  const [imageLayout, setImageLayout] = useState<ProductSectionImageLayout>(
    'SQUARE'
  );

  const checkedCheckbox = (
    <CheckCircle color="primary" style={{fontSize: '1.75rem'}} />
  );
  const uncheckedCheckbox = (
    <RadioButtonUnchecked style={{fontSize: '1.75rem'}} />
  );

  const handleFileChange = (event: SyntheticInputEvent<HTMLInputElement>) => {
    const target = event.target;
    const file = target.files[0];

    if (file) {
      setFile(file);
    } else {
      setFile(null);
    }
  };

  const handleInputClick = () => {
    if (inputRef.current) inputRef.current.click();
  };

  return (
    <CustomModal open={open} handleCloseModal={handleClose}>
      {file && (
        <CropModal
          open
          onClose={() => setFile(null)}
          aspect={ASPECT_RATIOS[imageLayout]}
          compressOptions={{
            quality: 0.8,
            maxWidth: 3840,
            maxHeight: 2160,
          }}
          images={[{preview: URL.createObjectURL(file), file}]}
          onSubmit={([result]) => onImageSave(result, imageLayout)}
        />
      )}
      <div className={classes.root}>
        <Typography align="left" className={classes.title}>
          Elige el formato de foto
        </Typography>
        <Divider className={classes.divider} />

        <Grid container spacing={2}>
          {SECTION_IMAGE_LAYOUTS.map(el => {
            const isSelected = el.name === imageLayout;
            return (
              <Grid key={el.name} item xs={12} md={4}>
                <CustomButton
                  className={`${classes.btn} ${
                    isSelected ? classes.selected : ''
                  }`}
                  onClick={() => {
                    setImageLayout(el.name);
                    setFile(null);
                  }}
                >
                  <div className={classes.checkboxWrap}>
                    {isSelected ? checkedCheckbox : uncheckedCheckbox}
                  </div>
                  <div className={classes.contentImg}>
                    <img src={el.img} alt="section layout" />
                  </div>
                </CustomButton>
              </Grid>
            );
          })}
        </Grid>
        <div className={classes.saveBtnWrap}>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={handleFileChange}
          />
          <CustomButton
            onClick={handleInputClick}
            variant="contained"
            className={classes.saveBtn}
          >
            Continuar
          </CustomButton>
        </div>
      </div>
    </CustomModal>
  );
}
